import { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut } from "lucide-react";

const emptyForm = {
  id: null,
  name: "",
  description: "",
  price: 0,
  category: "tees",
  sizes: "S,M,L,XL",
  images: "",
  stock: 100,
  featured: false,
  tagline: "",
};

export default function Admin() {
  const token = typeof window !== "undefined" ? localStorage.getItem("ts_admin_token") : null;
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});

  useEffect(() => {
    if (!token) return;
    loadProducts();
    loadOrders();
  }, [token]);

  if (!token) return <Navigate to="/admin/login" replace />;

  const startEdit = (p) => {
    setForm({
      id: p.id,
      name: p.name,
      description: p.description,
      price: p.price,
      category: p.category,
      sizes: p.sizes.join(","),
      images: p.images.join("\n"),
      stock: p.stock,
      featured: p.featured,
      tagline: p.tagline || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price),
      category: form.category,
      sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      stock: parseInt(form.stock, 10),
      featured: form.featured,
      tagline: form.tagline,
    };
    try {
      if (form.id) {
        await api.put(`/admin/products/${form.id}`, payload);
        toast.success("PRODUCT UPDATED");
      } else {
        await api.post("/admin/products", payload);
        toast.success("PRODUCT DROPPED");
      }
      setShowForm(false);
      setForm(emptyForm);
      loadProducts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("ELIMINATED");
      loadProducts();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const logout = () => {
    localStorage.removeItem("ts_admin_token");
    window.location.href = "/admin/login";
  };

  return (
    <div data-testid="admin-page" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between border-b border-white/15 pb-4 mb-6">
        <div>
          <p className="text-blood text-xs tracking-[0.3em] font-mono">// COMMAND CENTER</p>
          <h1 className="font-anton text-bone text-4xl uppercase tracking-tighter">TOXIC ADMIN</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-ash hover:text-blood">
          <LogOut size={14} /> LOGOUT
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          data-testid="admin-tab-products"
          onClick={() => setTab("products")}
          className={`px-5 py-2 text-xs tracking-[0.2em] uppercase font-mono border ${tab === "products" ? "bg-blood text-bone border-blood" : "border-white/20 text-bone"}`}
        >
          PRODUCTS ({products.length})
        </button>
        <button
          data-testid="admin-tab-orders"
          onClick={() => setTab("orders")}
          className={`px-5 py-2 text-xs tracking-[0.2em] uppercase font-mono border ${tab === "orders" ? "bg-blood text-bone border-blood" : "border-white/20 text-bone"}`}
        >
          ORDERS ({orders.length})
        </button>
      </div>

      {tab === "products" && (
        <>
          <button
            data-testid="admin-new-product"
            onClick={() => { setForm(emptyForm); setShowForm(true); }}
            className="mb-4 inline-flex items-center gap-2 px-5 py-3 bg-bone text-ink text-xs tracking-[0.3em] uppercase hover:bg-blood hover:text-bone"
          >
            <Plus size={16} /> NEW DROP
          </button>

          <div className="border border-white/15 divide-y divide-white/10">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-4 p-4">
                <img src={p.images?.[0]} alt={p.name} className="w-16 h-20 object-cover border border-white/10" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-anton uppercase text-bone text-lg tracking-tight truncate">{p.name}</h3>
                  <p className="text-[11px] tracking-[0.2em] text-ash uppercase">// {p.category} / ${p.price.toFixed(2)} / STOCK {p.stock}</p>
                </div>
                <button data-testid={`edit-product-${p.id}`} onClick={() => startEdit(p)} className="text-bone hover:text-blood" aria-label="edit"><Pencil size={16} /></button>
                <button data-testid={`delete-product-${p.id}`} onClick={() => del(p)} className="text-bone hover:text-blood" aria-label="delete"><Trash2 size={16} /></button>
              </div>
            ))}
            {products.length === 0 && <p className="p-8 text-center text-ash">NO PRODUCTS YET</p>}
          </div>
        </>
      )}

      {tab === "orders" && (
        <div className="border border-white/15 divide-y divide-white/10">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-row-${o.id}`} className="p-4">
              <div className="flex justify-between gap-4 flex-wrap">
                <div>
                  <p className="font-anton uppercase text-bone text-lg">{o.full_name} <span className="text-ash text-xs">/ {o.email}</span></p>
                  <p className="text-[11px] tracking-[0.2em] text-ash uppercase mt-1">// {o.id}</p>
                  <p className="text-[11px] text-ash mt-1">{new Date(o.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-anton text-bone text-lg">${o.total.toFixed(2)}</p>
                  <span className={`inline-block mt-1 px-2 py-1 text-[10px] tracking-[0.2em] uppercase ${o.payment_status === "paid" ? "bg-blood text-bone" : "border border-white/20 text-ash"}`}>
                    {o.payment_status}
                  </span>
                </div>
              </div>
              <div className="mt-3 text-sm text-ash">
                {o.items.map((it, i) => (
                  <span key={i} className="font-mono">{it.name} ({it.size}×{it.quantity}){i < o.items.length - 1 ? " • " : ""}</span>
                ))}
              </div>
            </div>
          ))}
          {orders.length === 0 && <p className="p-8 text-center text-ash">NO ORDERS YET</p>}
        </div>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto py-10" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="product-form" className="bg-ink border border-white/20 w-full max-w-2xl mx-4 p-6 space-y-3">
            <div className="flex justify-between items-center mb-2">
              <h2 className="font-anton text-bone text-2xl uppercase">{form.id ? "EDIT" : "NEW"} DROP</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-bone hover:text-blood"><X size={20} /></button>
            </div>
            <input data-testid="pf-name" required placeholder="NAME" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
            <textarea data-testid="pf-description" required placeholder="DESCRIPTION" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
            <input data-testid="pf-tagline" placeholder="TAGLINE (optional)" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="pf-price" required type="number" step="0.01" placeholder="PRICE" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
              <input data-testid="pf-stock" required type="number" placeholder="STOCK" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
              <select data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none">
                <option value="tees">TEES</option>
                <option value="hoodies">HOODIES</option>
                <option value="caps">CAPS</option>
                <option value="accessories">GEAR</option>
              </select>
            </div>
            <input data-testid="pf-sizes" placeholder="SIZES (comma separated)" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
            <textarea data-testid="pf-images" placeholder="IMAGE URLS (one per line)" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} rows={3} className="w-full bg-surface border border-white/20 px-4 py-3 text-bone text-sm focus:border-blood focus:outline-none" />
            <label className="flex items-center gap-2 text-bone text-sm">
              <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-blood" />
              FEATURED
            </label>
            <button data-testid="pf-submit" type="submit" className="w-full px-6 py-3 bg-blood text-bone text-xs tracking-[0.3em] uppercase hover:bg-bone hover:text-ink">
              {form.id ? "UPDATE" : "DROP IT"} →
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
